'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

type AuthTab = 'login' | 'signup';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function AuthForm() {
  const [tab, setTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  });

  const handleLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully');
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      const message =
        error?.message === 'Invalid login credentials' ?'Invalid email or password' : error?.message ||'Sign in failed. Please try again.';
      loginForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', {
        message: 'Passwords do not match',
      });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      toast.success('Account created — welcome to Shipframe!');
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      const message =
        error?.message?.includes('already registered')
          ? 'An account with this email already exists' : error?.message ||'Sign up failed. Please try again.';
      signupForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error?.message || 'Google sign in failed');
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-12">
      <div className="w-full max-w-sm space-y-6">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-2">
          <AppLogo size={24} />
          <span className="font-semibold text-base text-foreground">Shipframe</span>
        </div>

        {/* Tab switcher */}
        <div className="flex rounded-lg bg-muted p-1 gap-1">
          {(['login', 'signup'] as AuthTab[]).map((t) => (
            <button
              key={`tab-${t}`}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-150 ${
                tab === t
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'login' ? 'Sign in' : 'Sign up'}
            </button>
          ))}
        </div>

        {/* Heading */}
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-foreground">
            {tab === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {tab === 'login' ?'Sign in to your Shipframe workspace' :'Start catching design regressions today'}
          </p>
        </div>

        {/* Google OAuth */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-lg border border-border bg-card text-foreground text-sm font-medium hover:bg-muted transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isGoogleLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          Continue with Google
        </button>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Login Form */}
        {tab === 'login' && (
          <form
            onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
            className="space-y-4 animate-fade-in"
          >
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                type="email"
                className="input-field"
                placeholder="you@company.com"
                {...loginForm.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {loginForm.formState.errors.email && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {loginForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <Link
                  href="/sign-up-login-screen"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  {...loginForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'At least 6 characters required' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {loginForm.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                className="w-4 h-4 rounded border-border bg-input accent-primary"
                {...loginForm.register('remember')}
              />
              <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                Remember me for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        )}

        {/* Signup Form */}
        {tab === 'signup' && (
          <form
            onSubmit={signupForm.handleSubmit(handleSignupSubmit)}
            className="space-y-4 animate-fade-in"
          >
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Full name
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="Alex Johnson"
                {...signupForm.register('name', {
                  required: 'Name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' },
                })}
              />
              {signupForm.formState.errors.name && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {signupForm.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Work email
              </label>
              <input
                type="email"
                className="input-field"
                placeholder="you@company.com"
                {...signupForm.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {signupForm.formState.errors.email && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {signupForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pr-10"
                  placeholder="Min. 8 characters"
                  {...signupForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'At least 8 characters required' },
                    pattern: {
                      value: /^(?=.*[A-Z])(?=.*[0-9])/,
                      message: 'Include at least one uppercase letter and one number',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {signupForm.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">
                Confirm password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  {...signupForm.register('confirmPassword', {
                    required: 'Please confirm your password',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {signupForm.formState.errors.confirmPassword && (
                <p className="text-xs font-medium" style={{ color: 'var(--severity-critical)' }}>
                  {signupForm.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Creating account…
                </>
              ) : (
                'Create account'
              )}
            </button>

            <p className="text-xs text-muted-foreground text-center">
              By signing up you agree to our{' '}
              <a href="#" className="text-foreground hover:text-primary transition-colors">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-foreground hover:text-primary transition-colors">
                Privacy Policy
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// Inline AppLogo fallback for auth screen (no sidebar)
function AppLogo({ size }: { size: number }) {
  return (
    <div
      className="rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
        <rect x="2" y="5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" className="text-foreground" />
        <rect x="10" y="10" width="12" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
        <rect x="10" y="10" width="4" height="4" className="fill-primary opacity-40" />
      </svg>
    </div>
  );
}